import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Columns, Download, ListChecks, PackagePlus, Upload } from 'lucide-react';
import { downloadStockTemplate } from '../components/stock-import/downloadTemplate';
import { useToast } from '../components/ui/Toast';
import { PageHeader } from '../components/ui/PageHeader';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { StockImportWizard } from '../components/stock-import/StockImportWizard';

/**
 * Screen — Stock import (Purchasing & Stock Management). The only file path allowed to change stock:
 * each line posts an immutable ledger movement (a goods receipt or an inventory adjustment), never an
 * overwrite of a quantity column. Any file layout is accepted: the wizard suggests which column
 * feeds which field and the operator confirms it before anything is posted.
 */
export default function StockImport() {
  const { t } = useTranslation();
  const { showApiError } = useToast();
  const [open, setOpen] = useState(false);

  const downloadTemplate = async () => {
    try {
      await downloadStockTemplate();
    } catch (err) {
      showApiError(err);
    }
  };

  const steps = [
    { icon: Upload, title: t('import.stock.page.step1'), body: t('import.stock.page.step1Body') },
    { icon: Columns, title: t('import.stock.page.step2'), body: t('import.stock.page.step2Body') },
    {
      icon: ListChecks,
      title: t('import.stock.page.step3'),
      body: t('import.stock.page.step3Body'),
    },
  ];

  return (
    <div>
      <PageHeader
        title={t('import.stock.title')}
        description={t('import.stock.description')}
        actions={
          <Button size="sm" onClick={() => setOpen(true)}>
            <Upload className="h-4 w-4" /> {t('import.stock.importFile')}
          </Button>
        }
      />

      <Card className="p-8">
        <div className="mx-auto flex max-w-3xl flex-col items-center gap-6 text-center">
          <PackagePlus className="h-8 w-8 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">{t('import.stock.cardTitle')}</p>
            <p className="mx-auto max-w-lg text-xs text-muted-foreground">
              {t('import.stock.page.body')}
            </p>
          </div>
          <ol className="grid w-full gap-3 text-left sm:grid-cols-3">
            {steps.map((s, i) => (
              <li key={i} className="rounded-lg border bg-muted/30 p-3">
                <div className="mb-1 flex items-center gap-2 text-sm font-medium">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/15 text-[11px] font-semibold text-primary">
                    {i + 1}
                  </span>
                  <s.icon className="h-4 w-4 text-muted-foreground" />
                  {s.title}
                </div>
                <p className="text-xs text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="flex flex-wrap justify-center gap-2">
            <Button onClick={() => setOpen(true)}>
              <Upload className="h-4 w-4" /> {t('import.stock.start')}
            </Button>
            <Button variant="outline" onClick={() => void downloadTemplate()}>
              <Download className="h-4 w-4" /> {t('import.stock.wizard.template')}
            </Button>
          </div>
          <p className="max-w-lg text-xs text-muted-foreground">
            {t('import.stock.page.templateHint')}
          </p>
        </div>
      </Card>

      <StockImportWizard open={open} onClose={() => setOpen(false)} />
    </div>
  );
}
